import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL, API_URL } from '../utils/apiConfig';

const API = API_URL;

const SimpleCalendlyStyleDashboard = () => {
  const [activeTab, setActiveTab] = useState('availability');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Mock provider info
  const providerInfo = {
    id: 'demo-provider-001',
    name: 'Excellence Medical Center',
    facility_type: 'healthcare_clinic'
  };

  // Generate next 30 days for date selection
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const dates = generateDates();

  // Generate time slots for a day
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Load mock data
  useEffect(() => {
    // Mock availability data - some random available slots
    const mockAvailability = {
      '2025-01-31': ['09:00', '09:30', '10:00', '10:30', '14:00', '14:30', '15:00'],
      '2025-02-01': ['08:00', '08:30', '09:00', '11:00', '11:30', '16:00', '16:30'],
      '2025-02-03': ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00']
    };
    
    const mockAppointments = [
      {
        id: 'apt-001',
        date: '2025-01-31',
        time: '10:00',
        patient_name: 'Sarah Johnson',
        service: 'General Consultation',
        status: 'confirmed'
      },
      {
        id: 'apt-002',
        date: '2025-02-01',
        time: '09:00',
        patient_name: 'Ahmed Al-Rashid',
        service: 'Follow-up',
        status: 'confirmed'
      }
    ];

    setAvailability(mockAvailability);
    setAppointments(mockAppointments);
  }, []);

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const getDateKey = (date) => {
    return formatDate(date);
  };

  const isTimeAvailable = (time) => {
    const dateKey = getDateKey(selectedDate);
    return availability[dateKey]?.includes(time) || false;
  };

  const isTimeBooked = (time) => {
    const dateKey = getDateKey(selectedDate);
    return appointments.some(apt => apt.date === dateKey && apt.time === time);
  };

  const toggleTimeSlot = (time) => {
    const dateKey = getDateKey(selectedDate);
    const currentSlots = availability[dateKey] || [];
    
    if (currentSlots.includes(time)) {
      // Remove time slot
      const newSlots = currentSlots.filter(slot => slot !== time);
      setAvailability({
        ...availability,
        [dateKey]: newSlots
      });
    } else {
      // Add time slot
      setAvailability({
        ...availability,
        [dateKey]: [...currentSlots, time].sort()
      });
    }
  };

  const saveAvailability = async () => {
    setLoading(true);
    try {
      // Mock API call
      console.log('Saving availability:', availability);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      alert('Availability saved successfully!');
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Failed to save availability');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900">{providerInfo.name}</h1>
          <p className="text-gray-600">Manage your availability - Simple & Easy</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="flex bg-gray-100 rounded-lg p-1 mb-8 max-w-md">
          <button
            onClick={() => setActiveTab('availability')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'availability'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📅 Set Availability
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'appointments'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📋 Appointments
          </button>
        </div>

        {activeTab === 'availability' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Date Selection */}
            <div className="lg:col-span-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Date</h3>
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {dates.map((date) => (
                    <button
                      key={date.toISOString()}
                      onClick={() => setSelectedDate(date)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        isSelected(date)
                          ? 'bg-blue-50 border-blue-200 text-blue-900'
                          : isToday(date)
                          ? 'bg-green-50 border-green-200 text-green-900'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="font-medium">{formatDateDisplay(date)}</div>
                      <div className="text-sm opacity-75">
                        {isToday(date) && '(Today)'}
                        {availability[getDateKey(date)]?.length > 0 && 
                          ` • ${availability[getDateKey(date)].length} slots`
                        }
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Time Slot Selection */}
            <div className="lg:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Available Times - {formatDateDisplay(selectedDate)}
                </h3>
                <button
                  onClick={saveAvailability}
                  disabled={loading}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="mb-4">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      Available
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded"></div>
                      Booked
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-200 border-2 border-gray-300 rounded"></div>
                      Unavailable
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                  {timeSlots.map((time) => {
                    const available = isTimeAvailable(time);
                    const booked = isTimeBooked(time);
                    
                    return (
                      <button
                        key={time}
                        onClick={() => !booked && toggleTimeSlot(time)}
                        disabled={booked}
                        className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                          booked
                            ? 'bg-red-100 border-red-200 text-red-700 cursor-not-allowed'
                            : available
                            ? 'bg-green-100 border-green-200 text-green-800 hover:bg-green-200'
                            : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-200'
                        }`}
                      >
                        {time}
                        {booked && <div className="text-xs mt-1">Booked</div>}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">💡 Quick Tips:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Click time slots to make them available for booking</li>
                    <li>• Green slots = Available for users to book</li>
                    <li>• Red slots = Already booked by users</li>
                    <li>• Click "Save Changes" when you're done</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'appointments' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Upcoming Appointments ({appointments.length})
            </h3>
            
            {appointments.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg shadow-sm border">
                <div className="text-6xl mb-4">📅</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No appointments yet</h3>
                <p className="text-gray-600 mb-4">Set your availability first, then users can book with you</p>
                <button
                  onClick={() => setActiveTab('availability')}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Set Availability
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {appointment.patient_name}
                        </h4>
                        <div className="space-y-1 text-gray-600">
                          <p>📅 {new Date(appointment.date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}</p>
                          <p>⏰ {appointment.time}</p>
                          <p>🩺 {appointment.service}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          appointment.status === 'confirmed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleCalendlyStyleDashboard;